/**
 * Mock BaaS business account creation — replace with API when backend is ready.
 */

import { baasProxy } from "./baasConsoleClient";
import {
  composeE164FromParts,
  createEmptyPhoneParts,
  isPhonePartsComplete,
  mapPhonePartsFromPayload,
} from "./baasPhoneParts";
import {
  hasAnyOwnerIdentityDocNumber,
  hasIdentityDocNumber,
  isBrCountry,
  isIdentityDocComplete,
  isUsCountry,
} from "../Pages/Private/BaasConsole/Entidades/baasEntidadeCreateVisibility";
import {
  BAAS_API_PAYMENT_RAILS,
  normalizePaymentRails,
  serializePaymentRails,
} from "../Pages/Private/BaasConsole/Entidades/entityCompanyTypes";
import { createMockBaasEntity } from "./baasEntidadesApi";

/** Default sample payload for embedded-bank business account creation. */
export const DEFAULT_BUSINESS_ACCOUNT_PAYLOAD = {
  is_root: false,
  business_details: {
    business_name: "VALUT GLOBAL LLC",
    dba_name: "Valut",
    ein: "123456789",
    ein_pending: false,
    registration_id: {
      number: "2024-000123456",
      country_code: "US",
    },
    legal_type: "llc",
    industry: "financial_services",
    description: "Global financial infrastructure and embedded banking platform.",
    website: "https://valutglobal.com",
    address: {
      line_1: "101 Market St",
      line_2: "Suite 1913",
      city: "San Francisco",
      state: "CA",
      postal_code: "94105",
      country_code: "US",
    },
    has_virtual_address: false,
    country_of_incorporation: "US",
    state_of_incorporation: "DE",
    date_of_incorporation: "2024-01-15",
    year_of_incorporation: "2024",
    countries_of_operation: ["US", "BR", "GB", "AE"],
    account_usage: "Cross-border payments, treasury and card issuing.",
    purpose_of_account: "payments,treasury,card_issuing",
    expected_payment_rails: "ach,wire,swift,sepa,pix",
    makes_international_payments: true,
    payment_volumes: "1000000-5000000_monthly_usd",
    source_of_funds: "Customer deposits and business revenue.",
    expected_use_of_funds: "International settlements and treasury.",
    end_customer_type: "business_and_consumer",
    is_high_risk: false,
    beneficial_owners: [
      {
        first_name: "Leandro",
        middle_name: "Silva",
        last_name: "Abade",
        date_of_birth: "1995-10-20",
        email: "business.owner@example.com",
        phone_number: "+5511999990000",
        ssn: null,
        country_of_citizenship: "BR",
        address: {
          line_1: "Alameda Rio Negro",
          line_2: "Sala 1201",
          city: "Barueri",
          state: "SP",
          postal_code: "06454-000",
          country_code: "BR",
        },
        passport: {
          number: "AB123456",
          country_code: "BR",
          expiration_date: "2030-01-01",
          issuing_region: "SP",
        },
        drivers_license: {
          number: "SP1234567890",
          country_code: "BR",
          expiration_date: "2031-05-20",
          issuing_region: "SP",
        },
        national_id: {
          number: "12345678901",
          country_code: "BR",
          expiration_date: "2035-01-01",
          issuing_region: "SP",
        },
        employment_status: "employed",
        occupation: "CEO",
        role: "owner",
        job_title: "Chief Executive Officer",
        income: [500000],
        source_of_income: ["business_income"],
        purpose_of_account: ["payments", "treasury", "card_issuing"],
        expected_payment_rails: "ach,wire,swift,sepa,pix",
        makes_international_payments: true,
        pep_status: "no",
        is_high_risk: false,
        is_beneficial_owner: true,
        is_control_person: true,
        ownership_percentage: 100,
      },
    ],
  },
};

export const BAAS_BUSINESS_LEGAL_TYPES = [
  "llc",
  "corporation",
  "partnership",
  "sole_proprietorship",
  "non_profit",
];

export const BAAS_BUSINESS_INDUSTRIES = [
  "financial_services",
  "cryptocurrency",
  "technology",
  "retail",
  "healthcare",
  "manufacturing",
  "other",
];

/** Alias used by form combos — keep in sync with locale keys under options.industry.* */
export const BAAS_INDUSTRY_OPTIONS = BAAS_BUSINESS_INDUSTRIES;

/** Industry codes grouped by KYB risk tier — drives required steps and fields. */
export const BAAS_INDUSTRY_RISK = {
  high: ["financial_services", "cryptocurrency", "money_services", "gambling"],
  medium: ["technology", "healthcare", "other"],
  low: ["retail", "manufacturing"],
};

/** Maximum beneficial owners allowed in the create form. */
export const MAX_BENEFICIAL_OWNERS = 10;

export const BAAS_ENTIDADE_WIZARD_STEPS = [
  "document",
  "companyIdentity",
  "incorporation",
  "countryAndType",
  "businessAddress",
  "activityAndUsage",
  "ownerPersonal",
  "ownerAddress",
  "ownerIdentity",
  "ownerAccount",
  "review",
];

const digitsOnlyBusinessDoc = (value) => String(value || "").replace(/\D/g, "");

/**
 * Brazilian CNPJ checksum validation (14 digits).
 * @param {string} value
 */
export const isValidBusinessCnpj = (value) => {
  const c = digitsOnlyBusinessDoc(value);
  if (c.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(c)) return false;

  const calc = (base, weights) => {
    let sum = 0;
    for (let i = 0; i < weights.length; i += 1) {
      sum += parseInt(base[i], 10) * weights[i];
    }
    const rem = sum % 11;
    return rem < 2 ? 0 : 11 - rem;
  };

  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  return calc(c, w1) === parseInt(c[12], 10) && calc(c, w2) === parseInt(c[13], 10);
};

/**
 * Formats CNPJ (or shorter tax id) digits while typing.
 * @param {string} value
 */
export const formatBusinessDocumentInput = (value) => {
  const digits = digitsOnlyBusinessDoc(value).slice(0, 14);
  if (digits.length <= 11) {
    return digits;
  }
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

/** Digits used for business entity document lookup (CNPJ / registration / EIN). */
export const getBusinessLookupDocumentDigits = (form) => {
  if (isUsCountry(form?.countryOfIncorporation)) {
    if (form?.einPending) return "";
    return digitsOnlyBusinessDoc(form?.ein);
  }
  return digitsOnlyBusinessDoc(form?.registrationId?.number);
};

const BASE_REQUIRED_FIELDS = [
  "countryOfIncorporation",
  "legalType",
  "businessName",
  "dateOfIncorporation",
  "address.line1",
  "address.city",
  "address.state",
  "address.postalCode",
  "address.countryCode",
  "industry",
  "countriesOfOperation",
  "accountUsage",
  "beneficialOwners.0.firstName",
  "beneficialOwners.0.lastName",
  "beneficialOwners.0.email",
  "beneficialOwners.0.address.line1",
  "beneficialOwners.0.address.city",
  "beneficialOwners.0.address.state",
  "beneficialOwners.0.address.postalCode",
  "beneficialOwners.0.address.countryCode",
];

const MEDIUM_EXTRA_REQUIRED_FIELDS = [
  "sourceOfFunds",
  "beneficialOwners.0.purposeOfAccount",
];

const HIGH_EXTRA_REQUIRED_FIELDS = [
  "description",
  "website",
  "purposeOfAccount",
  "expectedPaymentRails",
  "paymentVolumes",
  "expectedUseOfFunds",
  "endCustomerType",
  "beneficialOwners.0.dateOfBirth",
  "beneficialOwners.0.phone",
  "beneficialOwners.0.countryOfCitizenship",
  "beneficialOwners.0.occupation",
  "beneficialOwners.0.jobTitle",
  "beneficialOwners.0.pepStatus",
  "beneficialOwners.0.ownershipPercentage",
];

const LOW_OPTIONAL_FIELDS = [
  "paymentVolumes",
  "sourceOfFunds",
  "expectedUseOfFunds",
  "expectedPaymentRails",
  "purposeOfAccount",
  "endCustomerType",
  "description",
  "website",
  "beneficialOwners.0.dateOfBirth",
  "beneficialOwners.0.phone",
  "beneficialOwners.0.passport",
  "beneficialOwners.0.driversLicense",
  "beneficialOwners.0.nationalId",
  "beneficialOwners.0.purposeOfAccount",
  "beneficialOwners.0.ownershipPercentage",
];

const ALWAYS_OPTIONAL_FIELDS = [
  "dbaName",
  "yearOfIncorporation",
  "hasVirtualAddress",
  "address.line2",
  "beneficialOwners.0.middleName",
  "beneficialOwners.0.ssn",
  "beneficialOwners.0.incomeAmount",
  "beneficialOwners.0.sourceOfIncome",
];

/** @param {string} industry */
export const getIndustryRiskLevel = (industry) => {
  if (BAAS_INDUSTRY_RISK.high.includes(industry)) {
    return "high";
  }
  if (BAAS_INDUSTRY_RISK.low.includes(industry)) {
    return "low";
  }
  return "medium";
};

/**
 * Returns KYB requirements for a given industry.
 * @param {string} industry
 */
export const getIndustryRequirements = (industry) => {
  const riskLevel = getIndustryRiskLevel(industry);
  const allSteps = [...BAAS_ENTIDADE_WIZARD_STEPS];

  if (riskLevel === "high") {
    return {
      riskLevel,
      requiredSteps: allSteps,
      optionalSteps: [],
      skippedSteps: [],
      requiredFields: [...BASE_REQUIRED_FIELDS, ...MEDIUM_EXTRA_REQUIRED_FIELDS, ...HIGH_EXTRA_REQUIRED_FIELDS],
      optionalFields: [...ALWAYS_OPTIONAL_FIELDS],
      autoSetHighRisk: true,
    };
  }

  if (riskLevel === "medium") {
    return {
      riskLevel,
      requiredSteps: allSteps.filter((step) => step !== "ownerIdentity"),
      optionalSteps: ["ownerIdentity"],
      skippedSteps: [],
      requiredFields: [...BASE_REQUIRED_FIELDS, ...MEDIUM_EXTRA_REQUIRED_FIELDS],
      optionalFields: [
        ...ALWAYS_OPTIONAL_FIELDS,
        "description",
        "website",
        "expectedPaymentRails",
        "paymentVolumes",
        "expectedUseOfFunds",
        "endCustomerType",
        "purposeOfAccount",
        "beneficialOwners.0.dateOfBirth",
        "beneficialOwners.0.phone",
        "beneficialOwners.0.passport",
        "beneficialOwners.0.driversLicense",
        "beneficialOwners.0.nationalId",
      ],
      autoSetHighRisk: false,
    };
  }

  return {
    riskLevel,
    requiredSteps: allSteps.filter((step) => !["ownerIdentity", "ownerAccount"].includes(step)),
    optionalSteps: ["ownerIdentity", "ownerAccount"],
    skippedSteps: ["ownerIdentity", "ownerAccount"],
    requiredFields: [...BASE_REQUIRED_FIELDS],
    optionalFields: [...ALWAYS_OPTIONAL_FIELDS, ...LOW_OPTIONAL_FIELDS],
    autoSetHighRisk: false,
  };
};

const getNestedValue = (obj, path) =>
  path.split(".").reduce((current, key) => (current == null ? undefined : current[key]), obj);

const isEmptyValue = (value) => {
  if (value == null) {
    return true;
  }
  if (typeof value === "string") {
    return !value.trim();
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === "number") {
    return !Number.isFinite(value);
  }
  // Split phone parts object from the entity forms
  if (
    typeof value === "object" &&
    ("countryDialCode" in value || "areaCode" in value)
  ) {
    return !isPhonePartsComplete(value);
  }
  return false;
};

/** @param {string} fieldPath @param {ReturnType<typeof getIndustryRequirements>} requirements */
export const isFieldRequiredForIndustry = (fieldPath, requirements) =>
  requirements.requiredFields.includes(fieldPath);

/** Checks if an owner field suffix is required (uses index 0 as template in requirements). */
export const isOwnerFieldRequired = (fieldSuffix, requirements) =>
  isFieldRequiredForIndustry(`beneficialOwners.0.${fieldSuffix}`, requirements);

/** @param {typeof EMPTY_BENEFICIAL_OWNER[]} owners */
export const getOwnershipTotal = (owners) =>
  (owners ?? []).reduce((sum, owner) => sum + (Number(owner.ownershipPercentage) || 0), 0);

/** @param {typeof EMPTY_BENEFICIAL_OWNER} owner @param {number} index */
export const getOwnerDisplayName = (owner, index) => {
  const name = [owner?.firstName, owner?.lastName].filter(Boolean).join(" ").trim();
  return name || null;
};

/** @param {string} step @param {ReturnType<typeof getIndustryRequirements>} requirements */
export const isStepSkippedForIndustry = (step, requirements) =>
  requirements.skippedSteps.includes(step);

/** @param {string} step @param {ReturnType<typeof getIndustryRequirements>} requirements */
export const isStepOptionalForIndustry = (step, requirements) =>
  requirements.optionalSteps.includes(step);

/**
 * Validates a single owner for owner-related wizard steps.
 * @returns {{ valid: boolean, errorKey?: string }}
 */
const validateOwnerForStep = (step, owner, industryRequirements) => {
  // Existing PERSON link: Baas only requires pers_id (+ role/ownership on account step).
  if (ownerHasLinkedPerson(owner) && step !== "ownerAccount") {
    return { valid: true };
  }

  switch (step) {
    case "ownerPersonal":
      if (!owner.firstName?.trim() || !owner.lastName?.trim() || !owner.email?.trim()) {
        return { valid: false, errorKey: "baas.entidades.create.ownerRequired" };
      }
      if (
        isOwnerFieldRequired("dateOfBirth", industryRequirements) &&
        !owner.dateOfBirth
      ) {
        return { valid: false, errorKey: "baas.entidades.create.ownerDateOfBirthRequired" };
      }
      if (isOwnerFieldRequired("phone", industryRequirements) && !isPhonePartsComplete(owner.phone)) {
        return { valid: false, errorKey: "baas.entidades.create.ownerPhoneRequired" };
      }
      break;

    case "ownerAddress": {
      const { line1, city, state, postalCode, countryCode } = owner.address ?? {};
      if (
        !line1?.trim() ||
        !city?.trim() ||
        !state?.trim() ||
        !postalCode?.trim() ||
        !countryCode?.trim()
      ) {
        return { valid: false, errorKey: "baas.entidades.create.ownerAddressRequired" };
      }
      break;
    }

    case "ownerIdentity": {
      const identityRequired = !isStepOptionalForIndustry("ownerIdentity", industryRequirements);
      if (!identityRequired) {
        break;
      }

      if (!hasAnyOwnerIdentityDocNumber(owner)) {
        return { valid: false, errorKey: "baas.entidades.create.identityDocRequired" };
      }

      const filledDocs = [owner.passport, owner.driversLicense, owner.nationalId].filter(
        hasIdentityDocNumber,
      );
      if (filledDocs.some((doc) => !isIdentityDocComplete(doc))) {
        return { valid: false, errorKey: "baas.entidades.create.identityDocIncomplete" };
      }
      break;
    }

    case "ownerAccount":
      if (
        isOwnerFieldRequired("purposeOfAccount", industryRequirements) &&
        !owner.purposeOfAccount?.length
      ) {
        return { valid: false, errorKey: "baas.entidades.create.ownerAccountRequired" };
      }
      break;

    default:
      break;
  }

  return { valid: true };
};

/**
 * BR create requires company email + phone (E.164) on business_details.
 * @returns {{ valid: boolean, errorKey?: string }}
 */
export const validateBrazilianBusinessContact = (form) => {
  if (!isBrCountry(form?.countryOfIncorporation)) {
    return { valid: true };
  }
  if (!String(form?.email || "").trim()) {
    return { valid: false, errorKey: "baas.entidades.create.emailRequired" };
  }
  if (!isPhonePartsComplete(form?.phone)) {
    return { valid: false, errorKey: "baas.entidades.create.phoneRequired" };
  }
  return { valid: true };
};

/**
 * Validates a single wizard step against industry requirements and country rules.
 * @returns {{ valid: boolean, errorKey?: string }}
 */
export const validateBusinessFormStep = (step, form, { visible, industryRequirements }) => {
  if (isStepSkippedForIndustry(step, industryRequirements)) {
    return { valid: true };
  }

  const ownerSteps = ["ownerPersonal", "ownerAddress", "ownerIdentity", "ownerAccount"];
  if (ownerSteps.includes(step)) {
    const owners = form.beneficialOwners ?? [];
    if (owners.length === 0) {
      return { valid: false, errorKey: "baas.entidades.create.ownerRequired" };
    }

    for (const owner of owners) {
      const result = validateOwnerForStep(step, owner, industryRequirements);
      if (!result.valid) {
        return result;
      }
    }

    if (step === "ownerAccount") {
      const total = getOwnershipTotal(owners);
      if (total > 100) {
        return { valid: false, errorKey: "baas.entidades.create.ownershipExceeds100" };
      }
      if (!owners.some(ownerIsBeneficialOwner)) {
        return { valid: false, errorKey: "baas.entidades.create.beneficialOwnerRequired" };
      }
    }

    return { valid: true };
  }

  switch (step) {
    case "document": {
      if (!form.countryOfIncorporation.trim()) {
        return { valid: false, errorKey: "baas.entidades.create.countryRequired" };
      }
      if (visible.ein && !form.ein.trim() && !form.einPending) {
        return { valid: false, errorKey: "baas.entidades.create.einRequired" };
      }
      if (visible.registrationId) {
        if (!form.registrationId.number.trim()) {
          return { valid: false, errorKey: "baas.entidades.create.registrationRequired" };
        }
        if (
          isBrCountry(form.countryOfIncorporation) &&
          !isValidBusinessCnpj(form.registrationId.number)
        ) {
          return { valid: false, errorKey: "baas.entidades.create.cnpjInvalid" };
        }
      }
      break;
    }

    case "countryAndType": {
      if (!form.countryOfIncorporation.trim()) {
        return { valid: false, errorKey: "baas.entidades.create.incorporationRequired" };
      }
      if (!form.legalType) {
        return { valid: false, errorKey: "baas.entidades.create.legalTypeRequired" };
      }
      break;
    }

    case "companyIdentity": {
      if (!form.businessName.trim()) {
        return { valid: false, errorKey: "baas.entidades.create.businessNameRequired" };
      }
      const contactResult = validateBrazilianBusinessContact(form);
      if (!contactResult.valid) {
        return contactResult;
      }
      break;
    }

    case "incorporation":
      if (!form.dateOfIncorporation) {
        return { valid: false, errorKey: "baas.entidades.create.incorporationRequired" };
      }
      break;

    case "businessAddress": {
      const { line1, city, state, postalCode, countryCode } = form.address;
      if (!line1.trim() || !city.trim() || !state.trim() || !postalCode.trim() || !countryCode.trim()) {
        return { valid: false, errorKey: "baas.entidades.create.addressRequired" };
      }
      break;
    }

    case "activityAndUsage":
      if (!form.industry) {
        return { valid: false, errorKey: "baas.entidades.create.industryRequired" };
      }
      if (!form.countriesOfOperation.length || !form.accountUsage.trim()) {
        return { valid: false, errorKey: "baas.entidades.create.operationsRequired" };
      }
      if (
        isFieldRequiredForIndustry("purposeOfAccount", industryRequirements) &&
        !form.purposeOfAccount?.length
      ) {
        return { valid: false, errorKey: "baas.entidades.create.processingActivitiesRequired" };
      }
      if (
        isFieldRequiredForIndustry("expectedPaymentRails", industryRequirements) &&
        !form.expectedPaymentRails?.length
      ) {
        return { valid: false, errorKey: "baas.entidades.create.paymentRailsRequired" };
      }
      if (
        isFieldRequiredForIndustry("sourceOfFunds", industryRequirements) &&
        !form.sourceOfFunds.trim()
      ) {
        return { valid: false, errorKey: "baas.entidades.create.sourceOfFundsRequired" };
      }
      if (
        isFieldRequiredForIndustry("paymentVolumes", industryRequirements) &&
        !form.paymentVolumes
      ) {
        return { valid: false, errorKey: "baas.entidades.create.paymentVolumesRequired" };
      }
      if (
        isFieldRequiredForIndustry("description", industryRequirements) &&
        !form.description.trim()
      ) {
        return { valid: false, errorKey: "baas.entidades.create.descriptionRequired" };
      }
      if (isFieldRequiredForIndustry("website", industryRequirements) && !form.website.trim()) {
        return { valid: false, errorKey: "baas.entidades.create.websiteRequired" };
      }
      break;

    default:
      break;
  }

  return { valid: true };
};

/**
 * Returns per-step completion for wizard tab indicators.
 * @param {string} step
 * @param {typeof EMPTY_BUSINESS_CREATE_FORM} form
 * @param {{ visible: ReturnType<import('../Pages/Private/BaasConsole/Entidades/baasEntidadeCreateVisibility').getVisibleFields>, industryRequirements: ReturnType<typeof getIndustryRequirements>, instanceId?: string }} context
 * @returns {'complete' | 'pending' | 'skipped' | 'optional'}
 */
export const getStepCompletionStatus = (step, form, { visible, industryRequirements, instanceId = "" }) => {
  if (isStepSkippedForIndustry(step, industryRequirements)) {
    return "skipped";
  }

  if (step === "review") {
    const hasPending = BAAS_ENTIDADE_WIZARD_STEPS.some((wizardStep) => {
      if (wizardStep === "review") {
        return false;
      }
      return (
        getStepCompletionStatus(wizardStep, form, { visible, industryRequirements, instanceId }) ===
        "pending"
      );
    });
    return hasPending ? "pending" : "complete";
  }

  if (isStepOptionalForIndustry(step, industryRequirements)) {
    if (step === "ownerIdentity") {
      const owners = form.beneficialOwners ?? [];
      const anyData = owners.some((item) => hasAnyOwnerIdentityDocNumber(item));
      if (!anyData) {
        return "optional";
      }
      const allSatisfied = owners.every((item) => {
        if (!hasAnyOwnerIdentityDocNumber(item)) {
          return true;
        }
        const docs = [item.passport, item.driversLicense, item.nationalId].filter(hasIdentityDocNumber);
        return docs.every(isIdentityDocComplete);
      });
      return allSatisfied ? "complete" : "pending";
    }
  }

  if (step === "document" || step === "companyIdentity") {
    const result = validateBusinessFormStep(step, form, { visible, industryRequirements });
    if (!result.valid || !instanceId) {
      return "pending";
    }
    return "complete";
  }

  const result = validateBusinessFormStep(step, form, { visible, industryRequirements });
  return result.valid ? "complete" : "pending";
};

/**
 * Collects all submit validation issues (for a clear list above the submit alert).
 * @returns {{ valid: boolean, errorKey?: string, issues: Array<{ step: string, errorKey: string }> }}
 */
export const collectBusinessSubmitIssues = (form, { visible, industryRequirements }) => {
  const issues = [];
  const seen = new Set();

  const pushIssue = (step, errorKey) => {
    const key = errorKey || "baas.entidades.create.incompleteRequiredFields";
    if (seen.has(key)) return;
    seen.add(key);
    issues.push({ step, errorKey: key });
  };

  for (const step of industryRequirements.requiredSteps) {
    if (step === "review") {
      continue;
    }
    const result = validateBusinessFormStep(step, form, { visible, industryRequirements });
    if (!result.valid) {
      pushIssue(step, result.errorKey);
    }
  }

  for (const fieldPath of industryRequirements.requiredFields) {
    if (
      fieldPath.startsWith("beneficialOwners.0.passport") ||
      fieldPath.startsWith("beneficialOwners.0.driversLicense") ||
      fieldPath.startsWith("beneficialOwners.0.nationalId")
    ) {
      continue;
    }

    if (fieldPath.startsWith("beneficialOwners.0.")) {
      const suffix = fieldPath.slice("beneficialOwners.0.".length);
      for (let index = 0; index < form.beneficialOwners.length; index += 1) {
        const value = getNestedValue(form, `beneficialOwners.${index}.${suffix}`);
        if (isEmptyValue(value)) {
          pushIssue("ownerPersonal", "baas.entidades.create.incompleteRequiredFields");
          break;
        }
      }
      continue;
    }

    const value = getNestedValue(form, fieldPath);
    if (isEmptyValue(value)) {
      pushIssue("review", "baas.entidades.create.incompleteRequiredFields");
    }
  }

  const ownershipTotal = getOwnershipTotal(form.beneficialOwners);
  if (ownershipTotal > 100) {
    pushIssue("ownerAccount", "baas.entidades.create.ownershipExceeds100");
  }

  if (visible.ein && !form.ein.trim() && !form.einPending) {
    pushIssue("document", "baas.entidades.create.einRequired");
  }

  if (visible.registrationId) {
    if (!form.registrationId.number.trim()) {
      pushIssue("document", "baas.entidades.create.registrationRequired");
    } else if (
      isBrCountry(form.countryOfIncorporation) &&
      !isValidBusinessCnpj(form.registrationId.number)
    ) {
      pushIssue("document", "baas.entidades.create.cnpjInvalid");
    }
  }

  return {
    valid: issues.length === 0,
    errorKey: issues[0]?.errorKey,
    issues,
  };
};

/**
 * Validates all required fields for submit based on industry tier.
 * @returns {{ valid: boolean, errorKey?: string, issues?: Array<{ step: string, errorKey: string }> }}
 */
export const validateBusinessFormForSubmit = (form, { visible, industryRequirements }) =>
  collectBusinessSubmitIssues(form, { visible, industryRequirements });

/** Finds next non-skipped step index. */
export const getNextWizardStepIndex = (currentIndex, industryRequirements) => {
  let next = currentIndex + 1;
  while (
    next < BAAS_ENTIDADE_WIZARD_STEPS.length &&
    isStepSkippedForIndustry(BAAS_ENTIDADE_WIZARD_STEPS[next], industryRequirements)
  ) {
    next += 1;
  }
  return Math.min(next, BAAS_ENTIDADE_WIZARD_STEPS.length - 1);
};

/** Finds previous non-skipped step index. */
export const getPreviousWizardStepIndex = (currentIndex, industryRequirements) => {
  let prev = currentIndex - 1;
  while (prev >= 0 && isStepSkippedForIndustry(BAAS_ENTIDADE_WIZARD_STEPS[prev], industryRequirements)) {
    prev -= 1;
  }
  return Math.max(prev, 0);
};

export const BAAS_COUNTRY_CODES = ["US", "BR", "GB", "AE", "DE", "CA", "MX", "FR", "ES", "PT"];

export const BAAS_EMPLOYMENT_STATUSES = [
  "employed",
  "self_employed",
  "unemployed",
  "retired",
  "student",
];

export const BAAS_OWNER_ROLES = ["owner", "director", "executive", "other"];

export const BAAS_PEP_STATUSES = ["no", "yes", "related"];

export const BAAS_END_CUSTOMER_TYPES = ["business", "consumer", "business_and_consumer"];

export const BAAS_PAYMENT_VOLUMES = [
  "0-100000_monthly_usd",
  "100000-500000_monthly_usd",
  "500000-1000000_monthly_usd",
  "1000000-5000000_monthly_usd",
  "5000000_plus_monthly_usd",
];

export const BAAS_SOURCE_OF_INCOME = [
  "business_income",
  "salary",
  "investments",
  "inheritance",
  "other",
];

export const BAAS_PURPOSE_OF_ACCOUNT = [
  "payments",
  "treasury",
  "card_issuing",
  "payroll",
  "other",
];

const parsePurposeOfAccountFromPayload = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (!value || typeof value !== "string") {
    return [];
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }
  const codes = trimmed.split(",").map((item) => item.trim()).filter(Boolean);
  const known = new Set(BAAS_PURPOSE_OF_ACCOUNT);
  if (codes.every((code) => known.has(code))) {
    return codes;
  }
  return [];
};

export const EMPTY_BUSINESS_ADDRESS = {
  line1: "",
  line2: "",
  number: "",
  neighborhood: "",
  city: "",
  state: "",
  postalCode: "",
  countryCode: "US",
};

export const EMPTY_IDENTITY_DOC = {
  number: "",
  countryCode: "",
  expirationDate: "",
  issuingRegion: "",
};

export const EMPTY_BENEFICIAL_OWNER = {
  /** When set (pers_…), links an existing PERSON instead of creating one. */
  persId: "",
  /** True for owners already on the entity (GET). Must not be re-POSTed. */
  fromServer: false,
  firstName: "",
  middleName: "",
  lastName: "",
  dateOfBirth: "",
  email: "",
  phone: createEmptyPhoneParts(),
  motherName: "",
  ssn: "",
  countryOfCitizenship: "",
  address: { ...EMPTY_BUSINESS_ADDRESS, countryCode: "BR" },
  passport: { ...EMPTY_IDENTITY_DOC },
  driversLicense: { ...EMPTY_IDENTITY_DOC },
  nationalId: { ...EMPTY_IDENTITY_DOC },
  employmentStatus: "employed",
  occupation: "",
  role: "owner",
  jobTitle: "",
  incomeAmount: "",
  sourceOfIncome: [],
  purposeOfAccount: [],
  expectedPaymentRails: "",
  makesInternationalPayments: false,
  pepStatus: "no",
  isHighRisk: false,
  isBeneficialOwner: true,
  isControlPerson: false,
  ownershipPercentage: "",
};

/** True when the owner row links an existing PERSON entity (pers_…). */
export const ownerHasLinkedPerson = (owner) =>
  Boolean(String(owner?.persId || "").trim().toLowerCase().startsWith("pers_"));

/** PaySmart / Conta Digital: control person counts as beneficial owner when the BO flag is omitted. */
export const resolveOwnerIsBeneficialOwner = (owner) => {
  const raw = owner?.is_beneficial_owner ?? owner?.isBeneficialOwner;
  const isControl = Boolean(owner?.is_control_person ?? owner?.isControlPerson);
  if (raw === undefined || raw === null) return true;
  return Boolean(raw) || isControl;
};

export const ownerIsBeneficialOwner = (owner) =>
  Boolean(owner?.isBeneficialOwner || owner?.isControlPerson);

export const isLastRequiredBeneficialOwner = (owners = [], index) => {
  const list = Array.isArray(owners) ? owners : [];
  const owner = list[index];
  if (!owner?.isBeneficialOwner) return false;
  return list.filter((item) => item?.isBeneficialOwner).length <= 1;
};

/** Ensures create payload always has at least one beneficiário final. */
export const ensureBeneficialOwnerFlags = (owners = []) => {
  const next = (Array.isArray(owners) ? owners : []).map((owner) => ({
    ...owner,
    isBeneficialOwner: Boolean(owner?.isBeneficialOwner || owner?.isControlPerson),
    isControlPerson: Boolean(owner?.isControlPerson),
  }));
  if (next.length > 0 && !next.some((owner) => owner.isBeneficialOwner)) {
    const controlIndex = next.findIndex((owner) => owner.isControlPerson);
    const index = controlIndex >= 0 ? controlIndex : 0;
    next[index] = { ...next[index], isBeneficialOwner: true };
  }
  return ensureSingleControlPerson(next);
};

/**
 * Exactly one Pessoa de controle: keep the first checked index (or `preferredIndex`),
 * clear control on everyone else. Checking one in the UI must deselect the others.
 */
export const ensureSingleControlPerson = (owners = [], preferredIndex = -1) => {
  const list = Array.isArray(owners) ? owners : [];
  if (list.length === 0) return list;

  let keepIndex = preferredIndex;
  if (keepIndex < 0 || keepIndex >= list.length || !list[keepIndex]?.isControlPerson) {
    keepIndex = list.findIndex((owner) => owner?.isControlPerson);
  }
  if (keepIndex < 0) return list;

  return list.map((owner, index) => ({
    ...owner,
    isControlPerson: index === keepIndex,
    isBeneficialOwner:
      index === keepIndex
        ? true
        : Boolean(owner?.isBeneficialOwner || owner?.isControlPerson),
  }));
};

/** Owners already on the BUSINESS (GET) — do not POST again on save. */
export const isExistingBusinessOwnerRow = (owner, persistedPersIds = new Set()) => {
  if (owner?.fromServer === true) return true;
  const persId = String(owner?.persId || "").trim().toLowerCase();
  return persId.startsWith("pers_") && persistedPersIds.has(persId);
};

/**
 * Owners to POST on entity edit (`POST …/beneficial-owners`).
 * Skips rows already linked; keeps new people and newly linked pers_ ids.
 */
export const selectNewBeneficialOwnersForSave = (owners = [], persistedPersIds = new Set()) =>
  (Array.isArray(owners) ? owners : []).filter((item) => {
    if (isExistingBusinessOwnerRow(item, persistedPersIds)) return false;
    if (ownerHasLinkedPerson(item)) return true;
    return Boolean(
      item.firstName?.trim() || item.lastName?.trim() || item.email?.trim(),
    );
  });

/** Creates a blank beneficial owner for the add-owner action. */
export const createEmptyBeneficialOwner = () => ({
  ...EMPTY_BENEFICIAL_OWNER,
  phone: { ...EMPTY_BENEFICIAL_OWNER.phone },
  address: { ...EMPTY_BENEFICIAL_OWNER.address },
  passport: { ...EMPTY_BENEFICIAL_OWNER.passport },
  driversLicense: { ...EMPTY_BENEFICIAL_OWNER.driversLicense },
  nationalId: { ...EMPTY_BENEFICIAL_OWNER.nationalId },
});

/**
 * Maps a PERSON entity (list/detail UI or raw GET) into a beneficial-owner form row
 * linked via pers_id.
 * @param {object} personEntity
 * @param {Partial<typeof EMPTY_BENEFICIAL_OWNER>} [overrides]
 */
export const mapPersonEntityToBeneficialOwner = (personEntity, overrides = {}) => {
  const raw = personEntity?.raw ?? personEntity ?? {};
  const details = raw.person_details ?? raw.personDetails ?? {};
  const persId = String(
    personEntity?.id || raw.id || raw.entity_id || details.pers_id || "",
  ).trim();
  const base = createEmptyBeneficialOwner();
  const fromPayload = mapOwnerFromPayload({
    pers_id: persId,
    first_name: details.first_name ?? details.firstName ?? personEntity?.name,
    middle_name: details.middle_name ?? details.middleName,
    last_name: details.last_name ?? details.lastName,
    date_of_birth: details.date_of_birth ?? details.dateOfBirth,
    email: details.email ?? personEntity?.email,
    phone: details.phone,
    phone_number: details.phone_number ?? details.phoneNumber,
    mother_name: details.mother_name ?? details.motherName,
    ssn: details.ssn,
    country_of_citizenship: details.country_of_citizenship ?? details.countryOfCitizenship,
    address: details.address,
    passport: details.passport,
    drivers_license: details.drivers_license ?? details.driversLicense,
    national_id: details.national_id ?? details.nationalId,
    employment_status: details.employment_status ?? details.employmentStatus,
    occupation: details.occupation,
    role: details.role,
    job_title: details.job_title ?? details.jobTitle,
  });

  // List rows may only have display name — split best-effort when details omit names.
  if (!fromPayload.firstName && !fromPayload.lastName && personEntity?.name) {
    const parts = String(personEntity.name).trim().split(/\s+/);
    fromPayload.firstName = parts[0] || "";
    fromPayload.lastName = parts.length > 1 ? parts.slice(1).join(" ") : "";
  }

  return {
    ...base,
    ...fromPayload,
    persId,
    fromServer: false,
    isBeneficialOwner: overrides.isBeneficialOwner ?? true,
    isControlPerson: overrides.isControlPerson ?? false,
    ownershipPercentage: overrides.ownershipPercentage ?? "",
    role: overrides.role ?? (fromPayload.role || "owner"),
    ...overrides,
    persId,
    fromServer: false,
  };
};

export const EMPTY_BUSINESS_CREATE_FORM = {
  isRoot: false,
  businessName: "",
  dbaName: "",
  email: "",
  phone: createEmptyPhoneParts(),
  ein: "",
  einPending: false,
  registrationId: { number: "", countryCode: "" },
  legalType: "llc",
  industry: "financial_services",
  description: "",
  website: "",
  address: { ...EMPTY_BUSINESS_ADDRESS },
  hasVirtualAddress: false,
  countryOfIncorporation: "",
  stateOfIncorporation: "",
  dateOfIncorporation: "",
  yearOfIncorporation: "",
  countriesOfOperation: [],
  accountUsage: "",
  purposeOfAccount: [],
  expectedPaymentRails: [],
  makesInternationalPayments: false,
  paymentVolumes: "1000000-5000000_monthly_usd",
  sourceOfFunds: "",
  expectedUseOfFunds: "",
  endCustomerType: "business_and_consumer",
  isHighRisk: false,
  beneficialOwners: [{ ...EMPTY_BENEFICIAL_OWNER }],
};

const mapAddressFromPayload = (address) => ({
  line1: address?.line_1 ?? address?.line1 ?? "",
  line2: address?.line_2 ?? address?.line2 ?? "",
  number: address?.number ?? "",
  neighborhood: address?.neighborhood ?? address?.line_2 ?? address?.line2 ?? "",
  city: address?.city ?? "",
  state: address?.state ?? "",
  postalCode: address?.postal_code ?? address?.postalCode ?? "",
  countryCode: address?.country_code ?? address?.countryCode ?? "",
});

const mapIdentityDocFromPayload = (doc) => ({
  number: doc?.number ?? "",
  countryCode: doc?.country_code ?? doc?.countryCode ?? "",
  expirationDate: doc?.expiration_date ?? doc?.expirationDate ?? "",
  issuingRegion: doc?.issuing_region ?? doc?.issuingRegion ?? "",
});

const splitPurposeOfAccount = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const mapOwnerExpectedPaymentRails = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim().toLowerCase()).filter(Boolean).join(",");
  }
  if (typeof value === "string") {
    return value.trim();
  }
  return "";
};

const mapOwnerFromPayload = (owner) => ({
  fromServer: true,
  persId: owner?.pers_id ?? owner?.persId ?? "",
  firstName: owner?.first_name ?? owner?.firstName ?? "",
  middleName: owner?.middle_name ?? owner?.middleName ?? "",
  lastName: owner?.last_name ?? owner?.lastName ?? "",
  dateOfBirth: owner?.date_of_birth ?? owner?.dateOfBirth ?? "",
  email: owner?.email ?? "",
  phone: mapPhonePartsFromPayload({
    phone: owner?.phone,
    phone_number: owner?.phone_number ?? owner?.phoneNumber,
  }),
  motherName: owner?.mother_name ?? owner?.motherName ?? "",
  ssn: owner?.ssn ?? "",
  countryOfCitizenship: owner?.country_of_citizenship ?? owner?.countryOfCitizenship ?? "",
  address: mapAddressFromPayload(owner?.address),
  passport: mapIdentityDocFromPayload(owner?.passport),
  driversLicense: mapIdentityDocFromPayload(owner?.drivers_license ?? owner?.driversLicense),
  nationalId: mapIdentityDocFromPayload(owner?.national_id ?? owner?.nationalId),
  employmentStatus: owner?.employment_status ?? owner?.employmentStatus ?? "employed",
  occupation: owner?.occupation ?? "",
  role: owner?.role ?? "owner",
  jobTitle: owner?.job_title ?? owner?.jobTitle ?? "",
  incomeAmount:
    Array.isArray(owner?.income) && owner.income.length > 0 ? String(owner.income[0]) : "",
  sourceOfIncome: Array.isArray(owner?.source_of_income)
    ? [...owner.source_of_income]
    : Array.isArray(owner?.sourceOfIncome)
      ? [...owner.sourceOfIncome]
      : [],
  purposeOfAccount: Array.isArray(owner?.purpose_of_account)
    ? [...owner.purpose_of_account]
    : Array.isArray(owner?.purposeOfAccount)
      ? [...owner.purposeOfAccount]
      : [],
  expectedPaymentRails: mapOwnerExpectedPaymentRails(
    owner?.expected_payment_rails ?? owner?.expectedPaymentRails,
  ),
  makesInternationalPayments: Boolean(
    owner?.makes_international_payments ?? owner?.makesInternationalPayments,
  ),
  pepStatus: owner?.pep_status ?? owner?.pepStatus ?? "no",
  isHighRisk: Boolean(owner?.is_high_risk ?? owner?.isHighRisk),
  isBeneficialOwner: resolveOwnerIsBeneficialOwner(owner),
  isControlPerson: Boolean(owner?.is_control_person ?? owner?.isControlPerson),
  ownershipPercentage: owner?.ownership_percentage ?? owner?.ownershipPercentage ?? 0,
});

/**
 * Prefills business create form from POST /v1/entities/autocomplete draft.
 * @param {ReturnType<typeof createInitialBusinessCreateForm>} form
 * @param {object|null} draft `{ business_details: ... }` snake_case from API
 */
export const applySerproBusinessDraftToForm = (form, draft) => {
  const details = draft?.business_details ?? draft?.businessDetails ?? draft ?? {};
  const base = form || createInitialBusinessCreateForm();
  const owners = Array.isArray(details.beneficial_owners)
    ? details.beneficial_owners
    : Array.isArray(details.beneficialOwners)
      ? details.beneficialOwners
      : [];

  const mappedOwners =
    owners.length > 0
      ? owners.map((owner) => {
          const mapped = mapOwnerFromPayload(owner);
          const empty = createEmptyBeneficialOwner();
          const jobTitle = mapped.jobTitle || "Administrador";
          return {
            ...empty,
            ...mapped,
            fromServer: false,
            ownershipPercentage: "",
            jobTitle,
            occupation: mapped.occupation || jobTitle,
            countryOfCitizenship: mapped.countryOfCitizenship || "BR",
            address: {
              ...empty.address,
              ...(mapped.address || {}),
              countryCode: mapped.address?.countryCode || "BR",
            },
            nationalId: {
              ...empty.nationalId,
              ...(mapped.nationalId || {}),
              countryCode: mapped.nationalId?.countryCode || "BR",
            },
            isControlPerson: mapped.isControlPerson !== false,
            isBeneficialOwner: mapped.isBeneficialOwner !== false,
          };
        })
      : [createEmptyBeneficialOwner()];

  return {
    ...base,
    businessName: details.business_name ?? details.businessName ?? base.businessName,
    dbaName: details.dba_name ?? details.dbaName ?? base.dbaName,
    email: details.email ?? base.email,
    phone: details.phone_number || details.phoneNumber || details.phone
      ? mapPhonePartsFromPayload({
          phone: details.phone,
          phone_number: details.phone_number ?? details.phoneNumber,
        })
      : base.phone,
    registrationId: {
      number:
        details.registration_id?.number
        ?? details.registrationId?.number
        ?? base.registrationId.number,
      countryCode:
        details.registration_id?.country_code
        ?? details.registrationId?.countryCode
        ?? base.registrationId.countryCode
        ?? "BR",
    },
    description: details.description ?? base.description,
    address: {
      ...base.address,
      ...mapAddressFromPayload(details.address),
    },
    countryOfIncorporation:
      details.country_of_incorporation
      ?? details.countryOfIncorporation
      ?? base.countryOfIncorporation
      ?? "BR",
    dateOfIncorporation:
      details.date_of_incorporation
      ?? details.dateOfIncorporation
      ?? base.dateOfIncorporation,
    beneficialOwners: mappedOwners,
  };
};

/**
 * Maps a BaaS GET /v1/entities/{id} business response into create-form state (for read-only detail).
 * @param {object} entityRaw
 */
export const mapBusinessEntityResponseToForm = (entityRaw) => {
  const details = entityRaw?.business_details ?? {};
  const base = createInitialBusinessCreateForm();
  const owners = Array.isArray(details.beneficial_owners) ? details.beneficial_owners : [];

  return {
    ...base,
    isRoot: Boolean(entityRaw?.is_root),
    businessName: details.business_name ?? "",
    dbaName: details.dba_name ?? "",
    email:
      details.email
      ?? entityRaw?.access_email?.real_email
      ?? entityRaw?.accessEmail?.realEmail
      ?? "",
    phone: mapPhonePartsFromPayload({
      phone: details.phone,
      phone_number: details.phone_number ?? details.phoneNumber,
    }),
    ein: details.ein ?? "",
    einPending: Boolean(details.ein_pending),
    registrationId: {
      number: details.registration_id?.number ?? "",
      countryCode: details.registration_id?.country_code ?? base.registrationId.countryCode,
    },
    legalType: details.legal_type || base.legalType,
    industry: details.industry || base.industry,
    description: details.description ?? "",
    website: details.website ?? "",
    address: {
      ...base.address,
      ...mapAddressFromPayload(details.address),
    },
    hasVirtualAddress: Boolean(details.has_virtual_address),
    countryOfIncorporation: details.country_of_incorporation ?? "",
    stateOfIncorporation: details.state_of_incorporation ?? "",
    dateOfIncorporation: details.date_of_incorporation ?? "",
    yearOfIncorporation: details.year_of_incorporation ?? "",
    countriesOfOperation: Array.isArray(details.countries_of_operation)
      ? [...details.countries_of_operation]
      : [],
    accountUsage: details.account_usage ?? "",
    purposeOfAccount: splitPurposeOfAccount(details.purpose_of_account),
    expectedPaymentRails: normalizePaymentRails(details.expected_payment_rails),
    makesInternationalPayments: Boolean(details.makes_international_payments),
    paymentVolumes: details.payment_volumes || base.paymentVolumes,
    sourceOfFunds: details.source_of_funds ?? "",
    expectedUseOfFunds: details.expected_use_of_funds ?? "",
    endCustomerType: details.end_customer_type || base.endCustomerType,
    isHighRisk: Boolean(details.is_high_risk),
    beneficialOwners:
      owners.length > 0 ? owners.map(mapOwnerFromPayload) : [createEmptyBeneficialOwner()],
  };
};

/**
 * Initializes editable form state for a new business entity.
 * Uses empty/intentional defaults only — sample payload stays on the debug JSON console.
 */
export const createInitialBusinessCreateForm = () => {
  const industryRequirements = getIndustryRequirements(EMPTY_BUSINESS_CREATE_FORM.industry);

  return {
    ...EMPTY_BUSINESS_CREATE_FORM,
    registrationId: { ...EMPTY_BUSINESS_CREATE_FORM.registrationId },
    phone: createEmptyPhoneParts(),
    address: { ...EMPTY_BUSINESS_ADDRESS },
    countriesOfOperation: [],
    purposeOfAccount: [],
    expectedPaymentRails: [],
    isHighRisk: Boolean(industryRequirements.autoSetHighRisk),
    beneficialOwners: [createEmptyBeneficialOwner()],
  };
};

/** @returns {string} Pretty-printed default business account payload JSON. */
export const getDefaultBusinessAccountPayloadJson = () =>
  JSON.stringify(DEFAULT_BUSINESS_ACCOUNT_PAYLOAD, null, 2);

/**
 * Parses and validates raw JSON for business account creation.
 * @param {string} jsonText
 */
export const parseBusinessAccountPayloadJson = (jsonText) => {
  const parsed = JSON.parse(jsonText);

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new SyntaxError("Invalid payload");
  }

  if (!parsed.business_details || typeof parsed.business_details !== "object") {
    throw new SyntaxError("Missing business_details");
  }

  return parsed;
};

const toE164Phone = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("55") && digits.length >= 12) return `+${digits}`;
  if (digits.startsWith("1") && digits.length >= 11) return `+${digits}`;
  return `+${digits}`;
};

const mapAddressToPayload = (address) => ({
  line_1: address.line1.trim(),
  line_2: address.line2?.trim() || null,
  number: address.number?.trim() || null,
  neighborhood: address.neighborhood?.trim() || null,
  city: address.city.trim(),
  state: address.state.trim(),
  postal_code: address.postalCode.trim(),
  country_code: address.countryCode.trim().toUpperCase(),
});

const inferOwnerDocCountry = (doc, owner) => {
  const fromDoc = String(doc?.countryCode || "").trim().toUpperCase();
  if (fromDoc) return fromDoc;
  const citizenship = String(owner?.countryOfCitizenship || "").trim().toUpperCase();
  if (citizenship) return citizenship;
  const addressCountry = String(owner?.address?.countryCode || "").trim().toUpperCase();
  if (addressCountry) return addressCountry;
  const digits = String(doc?.number || "").replace(/\D/g, "");
  if (digits.length === 11) return "BR";
  return "";
};

const mapIdentityDocToPayload = (doc, owner) => {
  const number = String(doc?.number ?? "").trim();
  if (!number) return null;
  const countryCode = inferOwnerDocCountry(doc, owner);
  if (!countryCode) return null;
  const payload = {
    number,
    country_code: countryCode,
  };
  const expiration = String(doc?.expirationDate || "").trim();
  if (expiration) payload.expiration_date = expiration;
  const region = String(doc?.issuingRegion || "").trim();
  if (region) payload.issuing_region = region;
  return payload;
};

/**
 * Maps a form beneficial-owner row to the BaaS owner payload
 * (create `business_details.beneficial_owners[]` or POST …/beneficial-owners).
 */
export const mapBeneficialOwnerToApiPayload = (owner, { isHighRisk = false } = {}) => {
  const persId = String(owner.persId || "").trim();
  if (persId.toLowerCase().startsWith("pers_")) {
    return {
      pers_id: persId,
      role: owner.role || "owner",
      job_title: owner.jobTitle?.trim() || null,
      is_beneficial_owner: Boolean(owner.isBeneficialOwner || owner.isControlPerson),
      is_control_person: Boolean(owner.isControlPerson),
      ownership_percentage: Number(owner.ownershipPercentage) || 0,
      pep_status: owner.pepStatus || null,
      is_high_risk: Boolean(isHighRisk),
    };
  }

  const incomeValue = Number(owner.incomeAmount);
  const income = Number.isFinite(incomeValue) && incomeValue > 0 ? [incomeValue] : [];

  const passportPayload = mapIdentityDocToPayload(owner.passport, owner);
  const driversLicensePayload = mapIdentityDocToPayload(owner.driversLicense, owner);
  const nationalIdPayload = mapIdentityDocToPayload(owner.nationalId, owner);
  const ownerPayload = {
    first_name: owner.firstName.trim(),
    middle_name: owner.middleName.trim(),
    last_name: owner.lastName.trim(),
    date_of_birth: owner.dateOfBirth || null,
    email: owner.email.trim(),
    phone_number: composeE164FromParts(owner.phone) || null,
    mother_name: owner.motherName?.trim() || null,
    ssn: owner.ssn.trim() || null,
    country_of_citizenship: owner.countryOfCitizenship.trim() || null,
    address: mapAddressToPayload(owner.address),
    employment_status: owner.employmentStatus,
    occupation: owner.occupation.trim() || null,
    role: owner.role,
    job_title: owner.jobTitle.trim() || null,
    income,
    source_of_income: [...(owner.sourceOfIncome ?? [])],
    purpose_of_account: [...(owner.purposeOfAccount ?? [])],
    expected_payment_rails: owner.expectedPaymentRails.trim() || null,
    makes_international_payments: Boolean(owner.makesInternationalPayments),
    pep_status: owner.pepStatus,
    is_high_risk: Boolean(isHighRisk),
    is_beneficial_owner: Boolean(owner.isBeneficialOwner || owner.isControlPerson),
    is_control_person: Boolean(owner.isControlPerson),
    ownership_percentage: Number(owner.ownershipPercentage) || 0,
  };

  if (passportPayload) ownerPayload.passport = passportPayload;
  if (driversLicensePayload) ownerPayload.drivers_license = driversLicensePayload;
  if (nationalIdPayload) ownerPayload.national_id = nationalIdPayload;

  return ownerPayload;
};

/**
 * Partial PATCH body for console entity edit (`PATCH /v1/entities/{id}`).
 * Omits identity (name / DBA). Drops console-only rails such as `ted`.
 * @param {typeof EMPTY_BUSINESS_CREATE_FORM} form
 */
export const buildBusinessEntityContactPatch = (form) => {
  const country = String(form.countryOfIncorporation || "")
    .trim()
    .toUpperCase();
  const rails = normalizePaymentRails(form.expectedPaymentRails).filter((rail) =>
    BAAS_API_PAYMENT_RAILS.includes(rail),
  );
  const registrationNumber = form.registrationId?.number?.trim() || "";
  const registrationCountry = String(form.registrationId?.countryCode || "")
    .trim()
    .toUpperCase();

  return {
    address: form.address,
    phone: form.phone,
    phoneNumber: composeE164FromParts(form.phone) || undefined,
    email: form.email?.trim() || null,
    country_of_incorporation: country || null,
    state_of_incorporation: form.stateOfIncorporation?.trim() || null,
    legal_type: form.legalType || null,
    industry: form.industry || null,
    date_of_incorporation: form.dateOfIncorporation || null,
    year_of_incorporation: form.yearOfIncorporation?.trim() || null,
    has_virtual_address: Boolean(form.hasVirtualAddress),
    expected_payment_rails: rails.length > 0 ? rails.map((rail) => rail.toUpperCase()) : null,
    purpose_of_account:
      form.purposeOfAccount?.length > 0 ? form.purposeOfAccount.join(",") : null,
    description: form.description?.trim() || null,
    website: form.website?.trim() || null,
    countries_of_operation: (form.countriesOfOperation ?? [])
      .map((code) => String(code || "").trim().toUpperCase())
      .filter(Boolean),
    account_usage: form.accountUsage?.trim() || null,
    payment_volumes: form.paymentVolumes || null,
    source_of_funds: form.sourceOfFunds?.trim() || null,
    expected_use_of_funds: form.expectedUseOfFunds?.trim() || null,
    end_customer_type: form.endCustomerType || null,
    makes_international_payments: Boolean(form.makesInternationalPayments),
    is_high_risk: Boolean(form.isHighRisk),
    ein_pending: Boolean(form.einPending),
    registration_id:
      registrationNumber || registrationCountry
        ? {
            number: registrationNumber || null,
            country_code: registrationCountry || null,
          }
        : null,
    ...buildBeneficialOwnerRolePatch(form.beneficialOwners),
  };
};

/**
 * PATCH `/v1/entities/{id}` body fragment for CONTROLE / beneficiário final flags
 * on already-linked `pers_` owners. Empty when there is no pers_id to update.
 */
export const buildBeneficialOwnerRolePatch = (owners = []) => {
  const beneficial_owners = ensureBeneficialOwnerFlags(owners)
    .filter((owner) => ownerHasLinkedPerson(owner))
    .map((owner) => ({
      pers_id: String(owner.persId || "").trim(),
      is_beneficial_owner: Boolean(owner.isBeneficialOwner || owner.isControlPerson),
      is_control_person: Boolean(owner.isControlPerson),
    }))
    .filter((row) => row.pers_id.toLowerCase().startsWith("pers_"));
  return beneficial_owners.length > 0 ? { beneficial_owners } : {};
};

/**
 * Maps UI form values into the embedded-bank business account payload shape.
 * Always returns the full API structure; unused optional fields are sent as empty/null defaults.
 * @param {typeof EMPTY_BUSINESS_CREATE_FORM} form
 * @param {{ industryRequirements?: ReturnType<typeof getIndustryRequirements> }} [options]
 */
export const buildBusinessAccountPayload = (form, options = {}) => {
  const industryRequirements = options.industryRequirements ?? getIndustryRequirements(form.industry);
  const isHighRisk =
    industryRequirements.autoSetHighRisk || Boolean(form.isHighRisk);

  const registrationNumber = form.registrationId.number.trim() || null;
  const registrationCountry = String(
    form.registrationId.countryCode || form.countryOfIncorporation || "",
  )
    .trim()
    .toUpperCase();
  const dateOfIncorporation = form.dateOfIncorporation || null;
  const yearOfIncorporation =
    form.yearOfIncorporation.trim() ||
    (dateOfIncorporation && /^\d{4}/.test(dateOfIncorporation)
      ? dateOfIncorporation.slice(0, 4)
      : null);

  return {
    is_root: Boolean(form.isRoot),
    business_details: {
      business_name: form.businessName.trim(),
      dba_name: form.dbaName.trim() || null,
      email: form.email?.trim() || null,
      phone_number: composeE164FromParts(form.phone) || null,
      ein: form.ein.replace(/\D/g, "") || null,
      ein_pending: Boolean(form.einPending),
      registration_id:
        registrationNumber || registrationCountry
          ? {
              number: registrationNumber,
              country_code: registrationCountry || null,
            }
          : null,
      legal_type: form.legalType,
      industry: form.industry,
      description: form.description.trim() || null,
      website: form.website.trim() || null,
      address: mapAddressToPayload(form.address),
      has_virtual_address: Boolean(form.hasVirtualAddress),
      country_of_incorporation: form.countryOfIncorporation.trim().toUpperCase(),
      state_of_incorporation: form.stateOfIncorporation.trim() || null,
      date_of_incorporation: dateOfIncorporation,
      year_of_incorporation: yearOfIncorporation,
      countries_of_operation: [...(form.countriesOfOperation ?? [])].map((code) =>
        String(code || "")
          .trim()
          .toUpperCase(),
      ),
      account_usage: form.accountUsage.trim(),
      purpose_of_account:
        form.purposeOfAccount?.length > 0 ? form.purposeOfAccount.join(",") : null,
      expected_payment_rails: serializePaymentRails(form.expectedPaymentRails) || null,
      makes_international_payments: Boolean(form.makesInternationalPayments),
      payment_volumes: form.paymentVolumes || null,
      source_of_funds: form.sourceOfFunds.trim() || null,
      expected_use_of_funds: form.expectedUseOfFunds.trim() || null,
      end_customer_type: form.endCustomerType || null,
      is_high_risk: isHighRisk,
      beneficial_owners: ensureBeneficialOwnerFlags(form.beneficialOwners).map((owner) =>
        mapBeneficialOwnerToApiPayload(owner, { isHighRisk }),
      ),
    },
  };
};

const formatDocument = (payload) => {
  const { business_details: details } = payload;
  if (details.ein) {
    const digits = details.ein.replace(/\D/g, "");
    if (digits.length === 9) {
      return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    }
    return details.ein;
  }
  return details.registration_id?.number ?? "";
};

/**
 * Creates a business entity via MobileService `/v1/ib/console/*` → BaaS `POST /v1/entities/business`.
 * @param {object} instanceOrId selected instance object (preferred)
 * @param {import('./baasBusinessAccountApi').CreateBusinessAccountPayload} payload
 */
export const createBusinessAccount = async (instanceOrId, payload) => {
  const instance =
    typeof instanceOrId === "object" && instanceOrId != null ? instanceOrId : null;

  if (!instance?.baasInstanceId) {
    throw new Error("createBusinessAccount requires the selected BaaS instance object");
  }

  const created = await baasProxy({
    instance,
    method: "POST",
    baasPath: "v1/entities/business",
    body: payload,
  });

  const { business_details: details } = payload;
  const entity = createMockBaasEntity(instance.id, {
    id: created?.id,
    name: details?.business_name || created?.name || created?.id,
    document: formatDocument(payload),
    type: "pj",
    status: "pending",
    kybStatus: "pending",
  });

  return { entity: { ...entity, id: created?.id || entity.id }, payload, raw: created };
};
